import { createTheme, type Theme, type Components } from '@mui/material/styles';
import type { BaseTextFieldProps } from '@mui/material';

/**
 * Interface to help type-check the secondary color logic in styleOverrides
 */
interface OwnerStateWithColor {
    ownerState: {
        color?: 'primary' | 'secondary' | string;
    };
}

export const getAppTheme = (portalContainer: HTMLElement | null): Theme => {
    return createTheme({
        components: {
            MuiPopover: { defaultProps: { container: portalContainer } },
            MuiModal: { defaultProps: { container: portalContainer } },
            MuiPopper: { defaultProps: { container: portalContainer } },

            MuiButton: {
                styleOverrides: {
                    root: {
                        fontSize: '16px',
                    },
                },
            },
            MuiLink: {
                styleOverrides: {
                    root: {
                        fontSize: '16px',
                    },
                },
            },
            MuiTextField: {
                defaultProps: {
                    variant: 'outlined',
                    fullWidth: true,
                } as BaseTextFieldProps,
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: ({ ownerState }: OwnerStateWithColor) => {
                        if (ownerState.color === 'secondary') {
                            return {
                                'backgroundColor': '#1d1d1d',
                                'borderRadius': '5px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#4B4B4B',
                                    borderWidth: '1px',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#4B4B4B',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#4B4B4B',
                                    borderWidth: '1px',
                                },
                                '& .MuiSelect-icon': {
                                    color: '#BBBBBB',
                                },
                            };
                        }
                        return {};
                    },
                    input: ({ ownerState }: OwnerStateWithColor) => {
                        if (ownerState.color === 'secondary') {
                            return {
                                'padding': '20px 24px',
                                'color': '#BBBBBB',
                                'fontSize': '16px',
                                'fontFamily': 'Effra, sans-serif',
                                'lineHeight': '20px',
                                '&:-webkit-autofill': {
                                    WebkitBoxShadow: '0 0 0 100px #1D1D1D inset !important',
                                    WebkitTextFillColor: '#D1D1D1 !important',
                                    caretColor: '#BBBBBB',
                                    borderRadius: 'inherit',
                                    transition: 'background-color 5000s ease-in-out 0s',
                                },
                                '&:-webkit-autofill:focus': {
                                    caretColor: '#BBBBBB',
                                },
                            };
                        }
                        return {};
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        'color': '#D1D1D1',
                        'fontFamily': 'Effra, sans-serif',
                        'fontSize': '16px',
                        'transform': 'translate(24px, 20px) scale(1)',
                        '&.Mui-focused': {
                            color: '#D1D1D1',
                        },
                        '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)',
                            backgroundColor: 'transparent',
                            padding: '0 4px',
                        },
                    },
                },
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    root: ({ ownerState }: OwnerStateWithColor) => ({
                        ...(ownerState.color === 'secondary' && {
                            'alignItems': 'flex-start',
                            'gap': '10px',
                            'margin': 0,
                            '& .MuiFormControlLabel-label': {
                                fontSize: '16px',
                                fontFamily: 'Effra, sans-serif',
                                color: '#BBBBBB',
                                lineHeight: '1.2',
                                marginTop: '5px',
                            },
                        }),
                    }),
                },
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: ({ ownerState }: OwnerStateWithColor) => ({
                        ...(ownerState.color === 'secondary' && {
                            'padding': 0,
                            'color': '#4B4B4B',
                            '&.Mui-checked': {
                                color: '#cc9900',
                            },
                            '& .MuiSvgIcon-root': {
                                fontSize: '20px',
                            },
                        }),
                    }),
                },
                defaultProps: {
                    checkedIcon: (
                        <div
                            style={{
                                width: 16,
                                height: 16,
                                border: '4px solid #fff',
                                backgroundColor: '#cc9900',
                                borderRadius: '2px',
                                boxSizing: 'border-box',
                            }}
                        />
                    ),
                    icon: (
                        <div
                            style={{
                                width: 16,
                                height: 16,
                                border: '1px solid #4B4B4B',
                                borderRadius: '2px',
                                backgroundColor: '#fff',
                            }}
                        />
                    ),
                },
            },
            MuiRadioGroup: {
                styleOverrides: {
                    root: {
                        gap: '16px',
                    },
                },
            },
            MuiRadio: {
                styleOverrides: {
                    root: ({ ownerState }: OwnerStateWithColor) => ({
                        ...(ownerState.color === 'secondary' && {
                            'padding': 0,
                            'color': '#4B4B4B',
                            '&.Mui-checked': {
                                color: '#cc9900',
                            },
                        }),
                    }),
                },
                defaultProps: {
                    icon: (
                        <div
                            style={{
                                width: 16,
                                height: 16,
                                border: '1px solid #4B4B4B',
                                borderRadius: '50%',
                                backgroundColor: '#fff',
                            }}
                        />
                    ),
                    checkedIcon: (
                        <div
                            style={{
                                width: 16,
                                height: 16,
                                border: '1px solid #cc9900',
                                borderRadius: '50%',
                                backgroundColor: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                            <div style={{ width: 8, height: 8, backgroundColor: '#cc9900', borderRadius: '50%' }} />
                        </div>
                    ),
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        backgroundColor: '#0B0B0B',
                        backgroundImage: 'none',
                        borderRadius: '5px',
                        color: '#D1D1D1',
                        fontFamily: 'Effra, sans-serif',
                        padding: '24px',
                        border: '1px solid #4A4A4A',
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        fontFamily: 'Effra, sans-serif',
                        fontSize: '20px',
                        fontWeight: 500,
                        color: '#D1D1D1',
                    },
                },
            },
            MuiDialogContent: {
                styleOverrides: {
                    root: {
                        fontFamily: 'Effra, sans-serif',
                        color: '#D1D1D1',
                    },
                },
            },
            MuiListItem: {
                styleOverrides: {
                    root: {
                        'paddingRight': '8px',
                        'paddingLeft': '8px',
                        'padding': '0px',
                        'border': '1px solid #bbbbbb',
                        'borderRadius': '16px',
                        'marginBottom': '6px',
                        '&.Mui-selected': {
                            borderColor: '#0b0b0b',
                        },
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        fontSize: '16px',
                        fontFamily: 'Effra, sans-serif',
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        // This affects the entire alert container
                        fontSize: '16px',
                        fontFamily: 'Effra, sans-serif',
                    },
                    message: {
                        // This specifically targets the text area
                        fontSize: '16px',
                    },
                    icon: {
                        // Optional: adjust icon size to match 16px text
                        fontSize: '20px',
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        // styles for all IconButtons
                    },
                    sizeMedium: {
                        zIndex: 10,
                        padding: '0px',
                        backgroundColor: '#0B0B0B',
                    },
                },
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        fontSize: '16px',
                        fontFamily: 'Effra, sans-serif',
                    },
                },
            },
        } as Components<Omit<Theme, 'components'>>,
        typography: {
            fontFamily: 'Effra, Helvetica, Arial, sans-serif',
            htmlFontSize: 16,
            fontSize: 16,
        },
    });
};
